/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the React component tree,
 * logs them, and displays a fallback UI instead of crashing the app.
 *
 * Critical for production stability and AI system reliability.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react'

import { logger } from '@/lib/logger'
interface Props {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details to console
    logger.error('ErrorBoundary caught an error:', error, errorInfo)

    // Update state with error details
    this.setState({
      error,
      errorInfo
    })

    // Call optional error handler
    this.props.onError?.(error, errorInfo)

    // In production, you might want to log to an error reporting service
    // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided by parent
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-gradient-to-b from-neutral-900 via-neutral-800 to-neutral-900 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-neutral-800 border border-red-500/30 rounded-lg p-8 shadow-2xl">
            {/* Error Icon */}
            <div className="flex items-center justify-center mb-6">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
            </div>

            {/* Error Title */}
            <h1 className="text-3xl font-bold text-red-400 text-center mb-4">
              Something Went Wrong
            </h1>

            {/* Error Description */}
            <p className="text-neutral-300 text-center mb-6">
              The application encountered an unexpected error. This has been logged
              and will be investigated.
            </p>

            {/* Error Details (Development Only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mb-6 bg-neutral-900 rounded-lg p-4 border border-neutral-700">
                <h3 className="text-sm font-semibold text-red-400 mb-2">
                  Error Details (Development Mode):
                </h3>
                <pre className="text-xs text-red-300 overflow-x-auto mb-3">
                  {this.state.error.toString()}
                </pre>
                {this.state.errorInfo && (
                  <>
                    <h4 className="text-xs font-semibold text-neutral-400 mb-1">
                      Component Stack:
                    </h4>
                    <pre className="text-xs text-neutral-400 overflow-x-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={this.handleReset}
                className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg
                         font-medium transition-colors duration-200 shadow-lg"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-6 py-3 bg-neutral-700 hover:bg-neutral-600 text-white rounded-lg
                         font-medium transition-colors duration-200"
              >
                Return Home
              </button>
            </div>

            {/* Additional Help */}
            <div className="mt-8 pt-6 border-t border-neutral-700">
              <p className="text-sm text-neutral-400 text-center">
                If this problem persists, please contact support or check the
                <a
                  href="/help"
                  className="text-amber-400 hover:text-amber-300 ml-1"
                >
                  help documentation
                </a>
              </p>
            </div>
          </div>
        </div>
      )
    }

    // No error, render children normally
    return this.props.children
  }
}

/**
 * Example Usage:
 *
 * 1. Wrap entire app:
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 *
 * 2. Wrap specific sections:
 * <ErrorBoundary>
 *   <DashboardSection />
 * </ErrorBoundary>
 *
 * 3. With custom fallback:
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <Component />
 * </ErrorBoundary>
 *
 * 4. With error handler:
 * <ErrorBoundary onError={(error, info) => logToService(error, info)}>
 *   <Component />
 * </ErrorBoundary>
 */
